// The plans, this week's usage, and the buttons that change them — on /agent, the page whose
// subject is the account. A user may hold TWO plans at once: their own (Free, Light, Pro, Heavy)
// and a seat on their team's plan. Each has its own spend and its own percentage, and the user
// chooses which one the agent draws from (the seat by default). Reads through the agent backend
// (/me/agent-usage → the LLM gateway, with the user's own key). Studio's Stripe checkout does the
// buying; cancel and resume of the personal plan are our own buttons.
// Every state renders — this card is the page's reason to exist, so it never vanishes.
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AGENT_SITE } from "../utils/landing";
import { Box, Button, CircularProgress, LinearProgress, Typography } from "@mui/material";
import { getAgentUsage, createCheckoutSession, setMeterAgainst, cancelPersonalPlan, resumePersonalPlan, getPlans, previewPlanChange, changePlan } from "../utils/api";
import { ACCOUNT_PATH } from "../utils/accountRedirect";
import { resetLabel } from "../utils/resetTime";
import { C, CARD, MICRO_LABEL } from "./memoryTheme";

const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : "—");
const money = (n) => (n == null ? "" : `$${Number(n)}/mo`);
const pct = (n) => Math.max(0, Math.min(100, Number(n) || 0));
const dateOf = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

const Bar = ({ value, strong }) => (
  <LinearProgress variant="determinate" value={pct(value)}
                  sx={{ height: 6, borderRadius: 3, bgcolor: "#e9edf1", "& .MuiLinearProgress-bar": { bgcolor: strong ? C.tealDeep : C.teal, borderRadius: 3 } }} />
);

export default function MemoryPlanCard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");   // loading | ok | none | error
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [plans, setPlans] = useState(null);       // { plans: [{plan, price_id, price_usd}], current, subscribed }
  const [review, setReview] = useState(null);     // a plan change awaiting confirmation: the preview
  const ref = useRef(null);
  const wantsUpgrade = searchParams.get("upgrade") === "1";
  const checkout = searchParams.get("checkout");

  const load = useCallback(async () => {
    try {
      setData(await getAgentUsage());
      setState("ok");
    } catch (e) {
      if (e?.message === "REDIRECTING_FOR_AUTH") return;
      setState(e?.response?.status === 404 ? "none" : "error");
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const loadPlans = useCallback(async () => {
    try { setPlans(await getPlans()); } catch { setPlans(null); }   // the chooser is optional; the card still works without it
  }, []);
  useEffect(() => { loadPlans(); }, [loadPlans]);

  // Back from Stripe: the webhook lands within seconds of the redirect. Re-read once shortly after
  // so the card shows the new plan without asking the user to refresh, then drop the param so a
  // reload does not re-announce it.
  useEffect(() => {
    if (checkout !== "success") return;
    const t = setTimeout(async () => {
      await load();
      const p = new URLSearchParams(searchParams);
      p.delete("checkout");
      setSearchParams(p, { replace: true });
    }, 4000);
    return () => clearTimeout(t);
  }, [checkout, load, searchParams, setSearchParams]);

  // Sent here by the agent's "Upgrade" (the 429, the account menu): land on the card.
  useEffect(() => {
    if (wantsUpgrade && state === "ok" && ref.current) ref.current.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [wantsUpgrade, state]);

  const run = async (fn, after = "") => {
    setBusy(true); setErr(""); setNote("");
    try { await fn(); await load(); if (after) setNote(after); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || "Something went wrong."); }
    finally { setBusy(false); }
  };
  // Picking a plan: with no live subscription it is a checkout for exactly that price; with one it is
  // a change in place, shown with the prorated bill first and made only on Confirm.
  const choose = async (plan) => {
    setBusy(true); setErr(""); setNote(""); setReview(null);
    try {
      const target = (plans?.plans || []).find((p) => p.plan === plan);
      if (!plans?.subscribed) {
        await createCheckoutSession(target?.price_id || null, { interval: "month", returnTo: ACCOUNT_PATH, product: "memory" });
        return;   // navigating to Stripe
      }
      setReview(await previewPlanChange(plan));
    } catch (e) { setErr(e?.response?.data?.error || e?.message || "Could not start the change."); }
    finally { setBusy(false); }
  };
  const confirmChange = async () => {
    if (!review) return;
    setBusy(true); setErr("");
    try {
      const r = await changePlan(review.plan);
      if (r?.checkout) { await createCheckoutSession(r.price_id, { interval: "month", returnTo: ACCOUNT_PATH, product: "memory" }); return; }
      setReview(null); setNote(`You're on ${cap(r?.plan || review.plan)}. The agent picks this up within a minute.`);
      await Promise.all([load(), loadPlans()]);
    } catch (e) { setErr(e?.response?.data?.error || e?.message || "Could not change the plan."); }
    finally { setBusy(false); }
  };
  const dollars = (cents) => `$${(Math.abs(Number(cents) || 0) / 100).toFixed(2)}`;

  if (state === "loading") {
    return (
      <Box sx={{ ...CARD, p: 2, mb: 2.5, display: "flex", alignItems: "center", gap: 1.5 }}>
        <CircularProgress size={16} sx={{ color: C.teal }} />
        <Typography sx={{ fontSize: 12.5, color: C.muted }}>Loading your plan…</Typography>
      </Box>
    );
  }
  // 404: the gateway has no record for this account yet — nothing has run on the agent.
  if (state === "none") {
    return (
      <Box sx={{ ...CARD, p: 2.25, mb: 2.5 }}>
        <Typography sx={MICRO_LABEL}>Plan</Typography>
        <Typography sx={{ color: C.ink, fontSize: 15, fontWeight: 600, mt: 0.5 }}>No plan on record yet</Typography>
        <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 0.5, lineHeight: 1.6 }}>
          Your plan and this week's usage appear here once the agent has run.{" "}
          <Box component="a" href={AGENT_SITE} sx={{ color: C.tealDeep }}>Get the agent</Box>
        </Typography>
      </Box>
    );
  }
  if (state === "error") {
    return (
      <Box sx={{ ...CARD, p: 2, mb: 2.5 }}>
        <Typography sx={{ fontSize: 12.5, color: C.muted }}>Plan details are unavailable right now.</Typography>
      </Box>
    );
  }

  // The gateway's two entitlements; an older gateway answers with just the active plan, and the
  // personal card is built from that.
  const ents = data?.entitlements || {};
  const personal = ents.personal || { plan: data?.plan, plan_label: data?.plan_label, price_usd: data?.price_usd, used_pct: data?.used_pct };
  const team = ents.team || null;
  const active = data?.active || (team && !ents.personal ? "team" : "personal");
  const switchable = Boolean(data?.switchable) && Boolean(team);
  const sub = data?.personal_subscription || null;                // the personal plan's Stripe state
  const free = personal.plan === "free" || Number(personal.price_usd) === 0;
  const label = personal.plan_label || cap(personal.plan);
  const pUsed = pct(personal.used_pct), tUsed = team ? pct(team.used_pct) : 0;
  const nearCap = (active === "team" ? tUsed : pUsed) >= 80;
  // Which entitlement the agent actually draws from — the one card this shows.
  const onTeam = Boolean(team) && active === "team";
  const shownUsed = onTeam ? tUsed : pUsed;
  const shownPrice = onTeam ? team.price_usd : (free ? null : personal.price_usd);
  // Both are being paid for: a seat the team buys AND a plan the person buys.
  const doubleBill = Boolean(team) && !free;
  const cancelling = Boolean(sub?.cancel_at_period_end);


  return (
    <Box ref={ref} sx={{ mb: 2.5 }}>
      {/* ONE card: what the agent actually draws from. Holding two entitlements at once is rare and
          something we now discourage, so it does not deserve a segmented control — that read as a view
          filter ("show me the other one") while it silently moved which subscription gets spent. The
          second entitlement is a sentence underneath instead, with the action inside it. */}
      <Box sx={{ ...CARD, p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap" }}>
          <Box>
            <Typography sx={MICRO_LABEL}>Your plan</Typography>
            <Typography sx={{ color: C.ink, fontSize: 18, fontWeight: 700, lineHeight: 1.3, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              {onTeam ? `Team seat · ${cap(team.seat)}` : label}
              {shownPrice != null && <Box component="span" sx={{ fontSize: 12.5, fontWeight: 500, color: C.muted }}>{money(shownPrice)}</Box>}
              {onTeam && <Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: C.muted }}>paid by your team</Box>}
              {checkout === "success" && <Box component="span" sx={{ fontSize: 12.5, fontWeight: 500, color: C.tealDeep }}>updating…</Box>}
            </Typography>
          </Box>
          {!onTeam && !free && sub && !cancelling ? (
            <Button size="small" onClick={() => run(cancelPersonalPlan, "")} disabled={busy} sx={{ textTransform: "none", color: C.muted, fontSize: 12.5 }}>
              Cancel plan
            </Button>
          ) : !onTeam && !free && !sub ? (
            // A paid plan with no Stripe subscription behind it — granted internally, or bought outside
            // this account. There is nothing for us to cancel, and rendering nothing at all leaves the
            // "cancel it" line pointing at empty space.
            <Typography sx={{ fontSize: 12, color: C.muted }}
                        title="This plan was not bought through this account, so Studio has no subscription to cancel.">
              not billed here
            </Typography>
          ) : null}
        </Box>

        {/* This week, counting UP: a number that grows reads as use; one that shrinks reads as a budget
            running out and makes people ration what they have already paid for. */}
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 12.5, color: shownUsed >= 80 ? C.tealDeep : C.body, fontWeight: shownUsed >= 80 ? 700 : 500 }}>
              {onTeam ? `${shownUsed}% of your seat's week used` : `${shownUsed}% of this week's included usage used`}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: C.muted }}>
              {[resetLabel(data) ? `resets ${resetLabel(data)}` : "",
                onTeam && team.pool_used_pct != null ? `team pool ${pct(team.pool_used_pct)}%` : ""].filter(Boolean).join(" · ")}
            </Typography>
          </Box>
          <Bar value={shownUsed} strong />
          {free && shownUsed >= 80 && (
            <Typography sx={{ fontSize: 12.5, color: C.body, mt: 1 }}>
              Almost there for the week. Upgrading keeps the agent going now; otherwise it resets {resetLabel(data) || "Monday"}.
            </Typography>
          )}
        </Box>

        {/* The plans for sale. The current one is marked; picking another starts checkout (no plan yet)
            or shows the prorated bill and waits for Confirm (plan held). */}
        {/* Only while the personal plan is the card being shown: this chooser changes THAT plan, and
            under a card headed "Team seat" it would silently edit something else. Hidden entirely for
            someone a seat covers who holds no plan of their own — the API refuses that purchase (one
            paid line per person), so the buttons could only ever fail. */}
        {plans?.plans?.length > 0 && !onTeam && (!team || sub) && (
          <Box sx={{ mt: 1.75 }} data-testid="plan-chooser">
            <Typography sx={MICRO_LABEL}>{free ? "Choose a plan" : "Change plan"}</Typography>
            {/* Only here: someone with no plan and no seat, looking at prices, is the only person who
                needs telling that the plugin does not ask for one. To everyone else it is noise. */}
            {free && !team && (
              <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 0.5 }}>
                For Assertion's own agent. The plugin needs no plan — personal memory is free.
              </Typography>
            )}
            <Box role="group" aria-label="Personal plans" sx={{ display: "flex", gap: 1, mt: 0.75, flexWrap: "wrap" }}>
              {/* One button per PLAN. The price map holds a monthly and a yearly price for each, and
                  both carry the same plan name, so anything that does not collapse them lists every
                  plan twice — the second labelled with the monthly price while charging for a year. */}
              {[...new Map(plans.plans.map((p) => [p.plan, p])).values()].map((p) => {
                const current = plans.subscribed && p.plan === plans.current;
                return (
                  <Button key={p.plan} size="small" variant={current ? "contained" : "outlined"} disabled={busy || current} aria-pressed={current}
                          onClick={() => choose(p.plan)}
                          sx={{ textTransform: "none", fontSize: 12.5, fontWeight: 600, px: 1.5, boxShadow: "none",
                                ...(current ? { bgcolor: C.teal, "&.Mui-disabled": { bgcolor: C.teal, color: "#fff" } } : { color: C.ink, borderColor: C.line }) }}>
                    {cap(p.plan)} <Box component="span" sx={{ fontWeight: 500, ml: 0.5, opacity: 0.85 }}>{money(p.price_usd)}</Box>
                    {current && <Box component="span" sx={{ fontSize: 11, ml: 0.75, opacity: 0.9 }}>current</Box>}
                  </Button>
                );
              })}
            </Box>
            {review && (
              <Box role="region" aria-label="Review plan change" sx={{ mt: 1.25, p: 1.5, border: `1px solid ${C.teal}`, borderRadius: 1.5, bgcolor: C.tealSoft }}>
                <Typography sx={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>
                  Switch to {cap(review.plan)} · {money(review.monthly_after_usd)}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: C.body, mt: 0.5 }}>
                  {review.preview_unavailable
                    ? "Stripe will prorate the difference for the rest of this period."
                    : review.due_today_cents == null
                      ? "Takes effect now."
                      : review.due_today_cents >= 0
                        ? `${dollars(review.due_today_cents)} due today for the rest of this period, then ${money(review.monthly_after_usd)}${review.period_end ? ` from ${dateOf(review.period_end * 1000)}` : ""}.`
                        : `A credit of ${dollars(review.due_today_cents)} for the rest of this period, then ${money(review.monthly_after_usd)}${review.period_end ? ` from ${dateOf(review.period_end * 1000)}` : ""}.`}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                  <Button size="small" variant="contained" onClick={confirmChange} disabled={busy}
                          sx={{ bgcolor: C.teal, textTransform: "none", fontWeight: 700, boxShadow: "none", "&:hover": { bgcolor: C.tealDeep } }}>
                    {busy ? "Changing…" : "Confirm"}
                  </Button>
                  <Button size="small" onClick={() => setReview(null)} disabled={busy} sx={{ textTransform: "none", color: C.muted }}>Keep {label}</Button>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {cancelling && (
          <Typography sx={{ fontSize: 12.5, color: C.body, mt: 1.25 }}>
            Cancelled{sub?.cancel_at ? ` — bills until ${dateOf(sub.cancel_at)}` : " at the end of this period"}, then this account is on Free
            {team ? " and your seat carries on" : ""}.{" "}
            <Box component="button" type="button" onClick={() => run(resumePersonalPlan, "Your plan continues.")} disabled={busy}
                 sx={{ border: 0, p: 0, bgcolor: "transparent", color: C.tealDeep, cursor: "pointer", font: "inherit", fontSize: 12.5 }}>
              Keep it
            </Box>
          </Typography>
        )}
      </Box>

      {/* The other entitlement: what it is, what it costs, how much of its week is gone, and the two
          things worth doing about it. A compact row rather than a second card — the point is that one
          of them is in use and the other is not, which twin cards flatten. */}
      {team && (
        <Box role="status"
             sx={{ mt: 1.25, p: 1.25, borderRadius: 1.5,
                   bgcolor: doubleBill ? "#fff8ec" : C.tealSoft,
                   border: `1px solid ${doubleBill ? "#f0dca8" : C.teal}`,
                   borderLeft: `3px solid ${doubleBill ? "#d9a93a" : C.teal}` }}>
          <Typography sx={{ fontSize: 12.5, color: doubleBill ? "#7a5d10" : C.body, lineHeight: 1.6 }}>
            {doubleBill && <b>Paid for twice. </b>}
            {onTeam
              ? (doubleBill
                  ? <>Your team pays for this seat, and you also pay for a plan of your own that the agent is not drawing from.</>
                  : <>Paid by your team. Seats are managed under Team below.</>)
              : (doubleBill
                  ? <>Your agent draws from the plan you pay for, while your team already pays for a seat that covers it.</>
                  : <>Your team pays for a seat you are not drawing from.</>)}
          </Typography>

          {/* the idle one, in full: name, price, and how much of its week has gone */}
          {(onTeam ? !free : true) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.ink }}>
                {onTeam ? label : `Team seat · ${cap(team.seat)}`}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: C.muted }}>
                {money(onTeam ? personal.price_usd : team.price_usd)}
                {onTeam ? " · yours" : " · paid by your team"}
              </Typography>
              <Box sx={{ flex: "1 1 120px", minWidth: 120, maxWidth: 240 }}>
                <Bar value={onTeam ? pUsed : tUsed} />
              </Box>
              <Typography sx={{ fontSize: 12, color: C.muted }}>
                {onTeam ? pUsed : tUsed}% of its week used
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.75, flexWrap: "wrap" }}>
            {switchable ? (
              <Box component="button" type="button" disabled={busy}
                   onClick={() => run(() => setMeterAgainst(onTeam ? "personal" : "team"), "Switched. The agent picks this up within a minute.")}
                   sx={{ border: 0, p: 0, bgcolor: "transparent", cursor: "pointer", font: "inherit", fontSize: 12.5,
                         fontWeight: 600, textDecoration: "underline", color: doubleBill ? "#9a6a0f" : C.tealDeep }}>
                {onTeam ? "Draw from this instead" : "Draw from the seat instead"}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 12, color: C.muted }}>Switching arrives with the next gateway release.</Typography>
            )}
            {doubleBill && sub && !cancelling && (
              <Box component="button" type="button" disabled={busy} onClick={() => run(cancelPersonalPlan, "")}
                   sx={{ border: 0, p: 0, bgcolor: "transparent", cursor: "pointer", font: "inherit", fontSize: 12.5,
                         fontWeight: 600, textDecoration: "underline", color: "#9a6a0f" }}>
                Cancel the personal plan
              </Box>
            )}
            {doubleBill && !sub && (
              <Typography sx={{ fontSize: 12, color: C.muted }}
                          title="This plan was not bought through this account, so Studio has no subscription to cancel.">
                not billed here
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {nearCap && !free && (
        <Typography sx={{ fontSize: 12.5, color: C.body, mt: 1 }}>
          Almost there for the week on the plan the agent draws from{team ? "; you can switch to the other below" : ""}.
        </Typography>
      )}
      {note && <Typography sx={{ fontSize: 12.5, color: C.tealDeep, mt: 1 }}>{note}</Typography>}
      {err && <Typography sx={{ fontSize: 12.5, color: "#b42318", mt: 1 }}>{err}</Typography>}
    </Box>
  );
}
